"use client";

import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

import { liveHomeCopy as enLiveHomeCopy } from "@/content/pages/en/live-home";
import { cn } from "@/lib/utils";

import { CardLede } from "./card-lede";
import { EQUITY_BARS } from "./equity-card";
import type { EquityBarDefinition } from "./equity-card";
import type { TermsCopy } from "./types";
import { useTermsCardMotion } from "./use-terms-card-motion";
import type {
  TermsMotionDebugProps,
  TermsMotionState,
} from "./use-terms-card-motion";

const EQUITY_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";
const EQUITY_DURATION_MS = 700;

const equityEase = cubicBezierEasing(0.22, 1, 0.36, 1);

export interface AnimatedEquityCardProps {
  copy?: TermsCopy;
  className?: string;
  motionDebug?: TermsMotionDebugProps;
}

function cubicBezierEasing(
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number
): (t: number) => number {
  const NEWTON_ITERATIONS = 4;

  function sampleCurveX(t: number) {
    return (
      ((1 - 3 * p2x + 3 * p1x) * t + (3 * p2x - 6 * p1x)) * t * t + 3 * p1x * t
    );
  }

  function sampleCurveY(t: number) {
    return (
      ((1 - 3 * p2y + 3 * p1y) * t + (3 * p2y - 6 * p1y)) * t * t + 3 * p1y * t
    );
  }

  function sampleCurveDerivativeX(t: number) {
    return (
      (3 * (1 - 3 * p2x + 3 * p1x) * t + 2 * (3 * p2x - 6 * p1x)) * t + 3 * p1x
    );
  }

  function solveCurveX(x: number) {
    let t = x;
    for (let i = 0; i < NEWTON_ITERATIONS; i += 1) {
      const slope = sampleCurveDerivativeX(t);
      if (slope === 0) break;
      t -= (sampleCurveX(t) - x) / slope;
    }
    return t;
  }

  return (t: number) => {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    return sampleCurveY(solveCurveX(t));
  };
}

function slugifyName(name: string): string {
  return name.split(" ")[0]?.toLowerCase() ?? name.toLowerCase();
}

function formatAnimatedPct(value: number, progress: number): string {
  if (value === 0) return "0%";
  const scaled = Math.round(value * progress * 10) / 10;
  return `${scaled.toFixed(1)}%`;
}

function formatMarkerLeft(barProgress: number): string {
  if (barProgress <= 0) return "0%";
  return `${(Math.round(barProgress * 1000) / 10).toFixed(1)}%`;
}

function resolveVisualProgress(
  state: TermsMotionState,
  phase: number | undefined,
  reducedMotion: boolean
): number {
  if (phase !== undefined) return phase;
  if (reducedMotion || state === "complete") return 1;
  if (state === "playing") return 1;
  return 0;
}

function shouldAnimateTransition(
  state: TermsMotionState,
  phase: number | undefined,
  reducedMotion: boolean
): boolean {
  return phase === undefined && !reducedMotion && state === "playing";
}

function useEquityLabelProgress(
  state: TermsMotionState,
  phase: number | undefined,
  reducedMotion: boolean,
  durationMs: number
): number {
  const [labelProgress, setLabelProgress] = useState(() => {
    if (phase !== undefined) return phase;
    return 1;
  });
  const playbackStartRef = useRef<number | null>(null);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    if (phase !== undefined) {
      setLabelProgress(phase);
      return;
    }

    if (reducedMotion || state === "complete") {
      setLabelProgress(1);
      return;
    }

    if (state === "idle") {
      setLabelProgress(0);
      return;
    }

    if (state !== "playing") return;

    playbackStartRef.current = performance.now();

    const tick = (timestamp: number) => {
      const start = playbackStartRef.current ?? timestamp;
      const linear = Math.min((timestamp - start) / durationMs, 1);
      setLabelProgress(equityEase(linear));
      if (linear < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [state, phase, reducedMotion, durationMs]);

  return phase !== undefined ? phase : labelProgress;
}

function AnimatedEquityRow({
  bar,
  visualProgress,
  labelProgress,
  animate,
}: {
  bar: EquityBarDefinition;
  visualProgress: number;
  labelProgress: number;
  animate: boolean;
}) {
  const slug = slugifyName(bar.name);
  const isActive = bar.value > 0;
  const barProgress = isActive ? (visualProgress * bar.value) / 100 : 0;
  const fillTransitionStyle = animate
    ? {
        transitionProperty: "transform, opacity",
        transitionDuration: `${EQUITY_DURATION_MS}ms`,
        transitionTimingFunction: EQUITY_EASING,
      }
    : undefined;
  const markerTransitionStyle = animate
    ? {
        transitionProperty: "left, opacity",
        transitionDuration: `${EQUITY_DURATION_MS}ms`,
        transitionTimingFunction: EQUITY_EASING,
      }
    : undefined;

  const rowStyle = {
    "--progress": barProgress,
    opacity: bar.muted ? labelProgress : 1,
  } as CSSProperties;

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={bar.muted ? "equity-row-wayflyer" : undefined}
      style={rowStyle}
    >
      <div className="text-grey-600 flex items-center justify-between text-[12px] leading-[1.2] tracking-[-0.04em]">
        <span className={bar.muted ? "text-grey-400" : "text-grey-600"}>
          {bar.name}
        </span>
        <span className={bar.muted ? "text-grey-400" : "text-grey-600"}>
          {formatAnimatedPct(bar.value, labelProgress)}
        </span>
      </div>
      <div
        className={cn(
          "relative w-full rounded-full bg-[#f6f6f5]",
          bar.trackClassName
        )}
      >
        {isActive && (
          <div
            data-testid={`equity-fill-${slug}`}
            className={cn(
              "h-full w-full origin-left rounded-full",
              bar.fillClassName
            )}
            style={{
              transform: "scaleX(var(--progress))",
              transformOrigin: "left",
              ...fillTransitionStyle,
            }}
          />
        )}
        {bar.marker && (
          <div className="pointer-events-none absolute inset-0">
            <span
              data-testid={`equity-marker-${slug}`}
              className="bg-grey-0 absolute top-1/2 flex size-[27px] items-center justify-center rounded-full shadow-[0px_6.6px_23.6px_0px_rgba(0,0,0,0.16)]"
              style={{
                left: formatMarkerLeft(barProgress),
                transform: "translate(-50%, -50%)",
                ...markerTransitionStyle,
              }}
            >
              {bar.marker}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function AnimatedEquityBars({
  className,
  visualProgress,
  labelProgress,
  animate,
}: {
  className?: string;
  visualProgress: number;
  labelProgress: number;
  animate: boolean;
}) {
  return (
    <div className={cn("flex flex-col gap-[18px]", className)}>
      {EQUITY_BARS.map((bar) => (
        <AnimatedEquityRow
          key={bar.name}
          bar={bar}
          visualProgress={visualProgress}
          labelProgress={labelProgress}
          animate={animate}
        />
      ))}
    </div>
  );
}

export function AnimatedEquityCard({
  copy = enLiveHomeCopy.terms,
  className,
  motionDebug,
}: AnimatedEquityCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { state, phase, reducedMotion } = useTermsCardMotion(
    ref,
    EQUITY_DURATION_MS,
    motionDebug
  );
  const visualProgress = resolveVisualProgress(state, phase, reducedMotion);
  const labelProgress = useEquityLabelProgress(
    state,
    phase,
    reducedMotion,
    EQUITY_DURATION_MS
  );
  const animate = shouldAnimateTransition(state, phase, reducedMotion);

  return (
    <div
      ref={ref}
      data-testid="terms-equity-animated"
      className={cn(
        "border-grey-100 bg-grey-0 relative h-[280px] overflow-hidden rounded-2xl border p-6 lg:h-[280px] lg:p-8",
        className
      )}
    >
      <CardLede
        lead={copy.equityLead}
        rest={copy.equityRest}
        className="max-w-[267px] lg:max-w-[304px]"
      />

      <AnimatedEquityBars
        visualProgress={visualProgress}
        labelProgress={labelProgress}
        animate={animate}
        className="absolute right-6 bottom-6 left-6 lg:right-8 lg:bottom-8 lg:left-8"
      />
    </div>
  );
}
